import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Box, Typography, Button, Paper, Divider, Stack, Tooltip } from '@mui/material';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';

const StatItem = ({ icon: Icon, label }) => (
  <Stack direction="row" spacing={1} alignItems="center" sx={{ color: '#0E2465' }}>
    <Icon sx={{ fontSize: 20 }} />
    <Typography variant="body2">{label}</Typography>
  </Stack>
);

const ServiceHeaderInfo = ({ service, isOwner, isFavorite, onToggleFavorite, onQuote }) => {
  const navigate = useNavigate();
  const { userId, serviceId } = useParams();
  const token = useSelector(state => state.auth.token);

  // Solo se puede reservar con sesión iniciada; si no, al login.
  const handleAdquirir = () => {
    if (!token) return navigate('/');
    navigate(`/servicio/${userId}/${serviceId}/reservar`);
  };
  const {
    nombre, costoServicio, tipoCosto, duracionEstimada, unidadDuracion, modalidades, idiomas, tags,
    ratingPromedio, ratingTotal
  } = service;

  return (
    <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: '12px', mb: 4 }} elevation={0} variant="outlined">
      {/* Top Row: Title, Rating, Recommendations, Price, Action.
          En móvil se apila (columna); en escritorio queda en una sola fila. */}
      <Box sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        justifyContent: 'space-between',
        alignItems: { xs: 'stretch', md: 'center' },
        gap: { xs: 1.5, md: 0 },
        mb: 2
      }}>
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h5" fontWeight="bold" color="#0E2465" sx={{ wordBreak: 'break-word' }}>
            {nombre}
          </Typography>
          {ratingPromedio > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
              <Typography variant="h6" color="#FFB800" sx={{ display: 'flex', alignItems: 'center' }}>★</Typography>
              <Typography variant="h6" fontWeight="bold" color="#0E2465">
                {ratingPromedio}
              </Typography>
            </Box>
          )}
        </Box>

        <Divider orientation="vertical" flexItem sx={{ mx: 3, display: { xs: 'none', md: 'block' } }} />

        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="body2" color="#737791">
            {ratingTotal > 0 ? `${ratingTotal} opinión${ratingTotal !== 1 ? 'es' : ''}` : 'Sin opiniones aún'}
          </Typography>
        </Box>

        <Divider orientation="vertical" flexItem sx={{ mx: 3, display: { xs: 'none', md: 'block' } }} />

        <Box sx={{
          flex: { md: 1.5 },
          width: { xs: '100%', md: 'auto' },
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: { xs: 'space-between', md: 'flex-end' },
          gap: { xs: 1.5, md: 3 }
        }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
            <Typography variant="h4" fontWeight="bold" color="#0E2465">
              ${Number(costoServicio || 0).toFixed(2)}
            </Typography>
            <Typography variant="caption" color="#737791" sx={{ fontSize: '14px' }}>
              {tipoCosto || 'por servicio'}
            </Typography>
          </Box>
          <Box onClick={onToggleFavorite} sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', flexShrink: 0 }}>
            {isFavorite ? (
              <FavoriteIcon color="primary" sx={{ fontSize: 28 }} />
            ) : (
              <FavoriteBorderIcon color="primary" sx={{ fontSize: 28 }} />
            )}
          </Box>
          {/* En móvil los botones toman su propia fila y se reparten el ancho */}
          <Box sx={{ display: 'flex', gap: 1.5, width: { xs: '100%', md: 'auto' } }}>
            <Button
              variant="outlined"
              disabled={isOwner}
              onClick={onQuote}
              sx={{
                flex: { xs: 1, md: 'none' },
                textTransform: 'none',
                borderColor: '#E2E8F0',
                color: '#475569',
                borderRadius: '8px',
                px: 3
              }}
            >
              Cotizar
            </Button>
            <Button
              variant="contained"
              disabled={isOwner}
              onClick={handleAdquirir}
              sx={{
                flex: { xs: 1, md: 'none' },
                bgcolor: '#0E2465',
                textTransform: 'none',
                borderRadius: '8px',
                px: 4,
                py: 1.2,
                fontWeight: 'bold',
                fontSize: '16px',
                whiteSpace: 'nowrap',
                '&.Mui-disabled': {
                  bgcolor: '#E2E8F0',
                  color: '#94A3B8'
                }
              }}
            >
              {isOwner ? 'Tu servicio' : 'Adquirir'}
            </Button>
          </Box>
        </Box>
      </Box>

      <Divider sx={{ mb: 2 }} />

      {/* Bottom Row: Stats Icons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 3 }}>
        <StatItem
          icon={AccessTimeIcon}
          label={`Duración: ${duracionEstimada || 3} ${unidadDuracion || 'horas'}`}
        />
        <StatItem
          icon={LocationOnIcon}
          label={modalidades?.join(', ') || 'Remoto'}
        />
        <StatItem
          icon={ChatBubbleOutlineIcon}
          label={idiomas?.join(', ') || 'Español'}
        />
        <StatItem
          icon={LocalOfferIcon}
          label={
            tags?.length > 3 ? (
              <Box component="span">
                {tags.slice(0, 3).join(', ')} 
                <Tooltip title={tags.join(', ')} arrow>
                  <Box component="span" sx={{ fontWeight: 'bold', cursor: 'pointer', ml: 0.5, color: '#0E2465' }}>
                    y {tags.length - 3} más...
                  </Box>
                </Tooltip>
              </Box>
            ) : (
              tags?.length > 0 ? tags.join(', ') : 'Servicio'
            )
          }
        />
      </Box>
    </Paper>
  );
};

export default ServiceHeaderInfo;
